import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Button, Typography } from "@material-ui/core";
import ColorizeIcon from "@material-ui/icons/Colorize";
import { getAppColor } from "@icarius-app/selectors";
import { getLocalizedString } from "@icarius-localization/strings";
import { SaveIcon } from "@icarius-icons";
import { getInitialDataAction } from "@icarius-app/actions";
import DynamicInput from "@icarius-common/dynamicInputs";
import { updateGeneralSettingsAction } from "../../actions";
import ChangeColorDialog from "../changeColorDialog";
import GenericSection from "./sections/genericSection";
import LogosAndCustomization from "./sections/logosAndCustomization";
import useHandleForm from "./useHandleForm";

const Form = ({ generalSettings }) => {
    const dispatch = useDispatch();
    const color = useSelector(getAppColor);

    const [changeColorDialogIsOpen, setChangeColorDialogIsOpen] = useState(false);

    const modify = (dataToSend) => {
        dispatch(updateGeneralSettingsAction(dataToSend)).then((resp) => {
            if (resp.status === 200) {
                dispatch(getInitialDataAction());
            }
        });
    };

    const { formData, setFormValue, submit } = useHandleForm(generalSettings, modify);

    if (!Boolean(generalSettings) || !Object.keys(formData)?.length) return null;

    return (
        <div id={"main-content"} style={{ padding: 50, paddingTop: 30 }}>
            {generalSettings?.activationDeactivation && (
                <GenericSection
                    title={getLocalizedString("activationDeactivation")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.activationDeactivation}
                />
            )}
            {generalSettings?.requests && (
                <GenericSection
                    title={getLocalizedString("requests")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.requests}
                />
            )}
            {generalSettings?.identificationAndSocietyData && (
                <GenericSection
                    title={getLocalizedString("identificationAndSocietyData")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.identificationAndSocietyData}
                />
            )}
            {generalSettings?.decimalFieldsDefinition && (
                <GenericSection
                    title={getLocalizedString("decimalFieldsDefinition")}
                    subtitle={getLocalizedString("decimals0To5")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.decimalFieldsDefinition}
                />
            )}
            {generalSettings?.digitalSign && (
                <GenericSection
                    title={getLocalizedString("digitalSign")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.digitalSign}
                />
            )}
            {generalSettings?.timezones && (
                <GenericSection
                    title={getLocalizedString("timezones")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.timezones}
                    fullSizeColumn
                />
            )}
            {generalSettings?.geographicOrganization && (
                <GenericSection
                    title={getLocalizedString("geographicStructureSettings")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.geographicOrganization}
                />
            )}
            {generalSettings?.logosAndCustomization && (
                <>
                    <LogosAndCustomization
                        title={getLocalizedString("logosAndCustomization")}
                        sectionFields={generalSettings.logosAndCustomization}
                        formData={formData}
                        setFormValue={setFormValue}
                    />
                    <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Button
                            startIcon={<ColorizeIcon />}
                            variant={"contained"}
                            className="whiteText"
                            onClick={() => setChangeColorDialogIsOpen(true)}
                        >
                            {getLocalizedString("setCorporateColor")}
                        </Button>
                    </div>
                </>
            )}
            {generalSettings?.permissions && (
                <GenericSection
                    title={getLocalizedString("permissionsGeneralParameters")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.permissions}
                />
            )}
            {generalSettings?.colaboratorPermissions && (
                <GenericSection
                    title={getLocalizedString("colaboratorPermissions")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.colaboratorPermissions}
                />
            )}
            {generalSettings?.peopleMaster && (
                <GenericSection
                    title={getLocalizedString("peopleMasterGeneralParameters")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.peopleMaster}
                />
            )}
            {generalSettings?.hideFieldsPeopleMaster && (
                <GenericSection
                    title={
                        <span
                            style={{
                                fontSize: "1rem",
                                textDecoration: "underline",
                                textTransform: "none",
                            }}
                        >
                            {getLocalizedString("hideFieldsPeopleMasterGeneralParameters")}
                        </span>
                    }
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.hideFieldsPeopleMaster}
                />
            )}
            {generalSettings?.historicalDataDuration && (
                <GenericSection
                    title={getLocalizedString("historicalDataDuration")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.historicalDataDuration}
                />
            )}
            {generalSettings?.mobileApps && (
                <GenericSection
                    title={getLocalizedString("mobileApps")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.mobileApps}
                />
            )}
            {generalSettings?.calculationProcesses && (
                <>
                    <GenericSection
                        title={getLocalizedString("payrollCalculationProcesses")}
                        formData={formData}
                        setFormValue={setFormValue}
                        sectionFields={generalSettings.calculationProcesses}
                    />
                    <div style={{ paddingTop: 8, width: "100%" }}>
                        <DynamicInput
                            fieldConfig={{
                                name: "DELETE_PROCESS_SQL",
                                type: "multiline",
                                editable: true,
                            }}
                            label={getLocalizedString("DELETE_PROCESS_SQL")}
                            value={formData["DELETE_PROCESS_SQL"]}
                            handleChange={setFormValue}
                        />
                        <Typography variant="caption" className="whiteText" style={{ paddingLeft: 10 }}>
                            {getLocalizedString("variableCodesContainsCalculationProcessCodesToBeDeleted")}
                        </Typography>
                    </div>
                </>
            )}
            {generalSettings?.incidences && (
                <GenericSection
                    title={getLocalizedString("fromIncidents")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.incidences}
                />
            )}
            {generalSettings?.orgchart && (
                <GenericSection
                    title={getLocalizedString("fromOrgChart")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.orgchart}
                />
            )}
            {generalSettings?.observations && (
                <GenericSection
                    title={getLocalizedString("timeManagementSettings")}
                    subtitle={getLocalizedString("doYouConsiderObservationsForAssistanceAnalysis")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.observations}
                />
            )}
            {generalSettings?.benefits && (
                <GenericSection
                    title={getLocalizedString("ofBenefits")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.benefits}
                />
            )}
            {generalSettings?.publications && (
                <GenericSection
                    title={getLocalizedString("fromPublications")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.publications}
                />
            )}
            {generalSettings?.specificSkillsPerformanceAndJobDescription && (
                <GenericSection
                    title={getLocalizedString("performanceCompetenciesDescription")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.specificSkillsPerformanceAndJobDescription}
                />
            )}
            {generalSettings?.goalsAndObjectives && (
                <GenericSection
                    title={getLocalizedString("goalsAndObjectives")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.goalsAndObjectives}
                />
            )}
            {generalSettings?.performanceRatings && (
                <GenericSection
                    title={getLocalizedString("fromPerformanceRatings")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.performanceRatings}
                />
            )}
            {generalSettings?.talentRanking && (
                <GenericSection
                    title={getLocalizedString("talentClassification9boxes")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.talentRanking}
                />
            )}
            {generalSettings?.talentRanking && (
                <GenericSection
                    title={getLocalizedString("performanceAndTalentAnalyticsSettings")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.performanceAnalyitics}
                />
            )}
            {generalSettings?.communicationChannelSettings && (
                <GenericSection
                    title={getLocalizedString("fromCommunicationChannel")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.communicationChannelSettings}
                />
            )}
            {generalSettings?.dataAnalytics && (
                <GenericSection
                    title={getLocalizedString("fromDataAnalytics")}
                    formData={formData}
                    setFormValue={setFormValue}
                    sectionFields={generalSettings.dataAnalytics}
                />
            )}
            <Grid container item xs={12} justify={"flex-end"}>
                <Button
                    className="whiteText"
                    style={{ cursor: "pointer" }}
                    onClick={submit}
                    variant="contained"
                    startIcon={<SaveIcon htmlColor={color} />}
                >
                    {getLocalizedString("save")}
                </Button>
            </Grid>
            {changeColorDialogIsOpen && (
                <ChangeColorDialog
                    open={changeColorDialogIsOpen}
                    companyColor={color}
                    handleCloseDialog={() => setChangeColorDialogIsOpen(false)}
                />
            )}
        </div>
    );
};

export default Form;
